// src/components/ShareModal.tsx
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonSpinner,
  useIonToast,
  IonInput,
  IonItem,
  IonLabel,
  IonRadioGroup,
  IonRadio,
  IonText,
} from '@ionic/react';
import { closeOutline, copyOutline, mailOutline, logoWhatsapp } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { user as userApi } from '../services/api.ts';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  resumeId: string | null;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, resumeId }) => {
  const [present] = useIonToast();
  const [loading, setLoading] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [title, setTitle] = useState('');
  const [accessControl, setAccessControl] = useState('public');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const fetchExistingShareSettings = async () => {
      if (!resumeId) return;

      setLoading(true);
      setShareLink('');

      // Reset to defaults first
      setTitle('');
      setAccessControl('public');

      try {
        // First, try to get existing share without creating/updating anything
        // We'll create a separate API call for fetching vs updating
        const response = await userApi.shareResume(resumeId, null, null); // null values mean "don't update"
        const shareToken = response.share_token;
        const link = `${window.location.origin}/profile/${shareToken}`;
        setShareLink(link);

        // Load the saved settings from the backend response
        console.log('Backend response:', response);
        setTitle(response.title || '');
        setAccessControl(response.access_control || 'public');
        console.log('Set access control to:', response.access_control || 'public');
      } catch (error) {
        console.error('Share API Error:', error);
        present({ message: 'Could not create share link. Please try again.', duration: 3000, color: 'danger' });
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && resumeId) {
      fetchExistingShareSettings();
    }
  }, [isOpen, resumeId, present]);

  // Fetch user info for personalized messages (WhatsApp only)
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!isOpen) return;

      try {
        const response = await userApi.getProfile();
        if (response.name) {
          setUserName(response.name);
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        // Continue without name - not critical
      }
    };

    if (isOpen) {
      fetchUserInfo();
    }
  }, [isOpen]);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink);
    present({ message: 'Link copied to clipboard!', duration: 2000, color: 'success' });
  };

  const handleShareGmail = () => {
    const subject = encodeURIComponent(title || 'My Professional Work Experience');
    // No name in email - Gmail shows sender's name and signature auto-appends
    const emailBody = `Hi,

I wanted to share my professional work experience with you.

You can view it here:
${shareLink}

Best regards`;

    const body = encodeURIComponent(emailBody);
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`, '_blank');
  };

  const handleShareWhatsApp = () => {
    // Include name in WhatsApp for context (casual messaging)
    const nameIntro = userName ? `I'm ${userName}.\n\n` : '';
    const message = `Hi,\n\n${nameIntro}I wanted to share my professional work experience with you:\n\n${shareLink}`;

    const text = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleUpdateLink = async (newAccessControl?: string, newTitle?: string) => {
    if (!resumeId) return;

    console.log('handleUpdateLink called with:', { newAccessControl, newTitle, currentAccessControl: accessControl, currentTitle: title });

    setLoading(true);
    try {
      const currentAccessControl = newAccessControl ?? accessControl;
      const currentTitle = newTitle ?? title;

      console.log('Making API call with:', { resumeId, currentTitle, currentAccessControl });

      // Add a timeout to the request
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      const apiPromise = userApi.shareResume(resumeId, currentTitle, currentAccessControl);

      const response = await Promise.race([apiPromise, timeoutPromise]);
      const shareToken = response.share_token;
      const link = `${window.location.origin}/profile/${shareToken}`;
      setShareLink(link);

      console.log('Update successful, response:', response);

      // Only show update message if user explicitly clicked update button
      if (newAccessControl === undefined && newTitle === undefined) {
        present({ message: 'Share settings updated!', duration: 2000, color: 'success' });
      }
    } catch (error) {
      console.error('Update failed:', error);
      // Only show error for manual updates, not auto-updates
      if (newAccessControl === undefined && newTitle === undefined) {
        present({ message: 'Could not update share settings.', duration: 3000, color: 'danger' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      breakpoints={[0, 0.65, 0.85, 1]}
      initialBreakpoint={0.85}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Share Resume Profile</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <IonSpinner />
            <p style={{ color: 'var(--app-text-color-secondary)', margin: 0 }}>
              Updating link setting...
            </p>
          </div>
        ) : (
          <div style={{
            maxWidth: '450px',
            margin: '0 auto',
            padding: '0 4px'
          }}>
            <IonItem lines="none" style={{ marginBottom: '16px' }}>
              <IonInput
                label="Profile Title (Optional)"
                labelPlacement="stacked"
                value={title}
                placeholder="e.g., Senior Engineer"
                onIonInput={(e) => setTitle(e.detail.value!)}
              />
            </IonItem>

            <div style={{ marginBottom: '16px' }}>
              <IonLabel style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem', color: 'var(--app-text-color)' }}>
                Access Control
              </IonLabel>
              <IonRadioGroup
                value={accessControl}
                onIonChange={async (e) => {
                  const newAccessControl = e.detail.value;
                  setAccessControl(newAccessControl);
                  // Auto-save when access control changes
                  await handleUpdateLink(newAccessControl, title);
                }}
              >
                <IonItem
                  button
                  onClick={async () => {
                    setAccessControl('public');
                    await handleUpdateLink('public', title);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setAccessControl('public');
                      handleUpdateLink('public', title);
                    }
                  }}
                  style={{
                    '--border-radius': '6px',
                    '--background': 'var(--app-card-background-color)',
                    '--padding-start': '12px',
                    '--padding-end': '12px',
                    '--min-height': '48px',
                    marginBottom: '8px',
                    border: accessControl === 'public' ? '2px solid var(--ion-color-primary)' : '1px solid var(--app-border-color)',
                    cursor: 'pointer'
                  }}
                >
                  <IonRadio
                    slot="start"
                    value="public"
                    style={{ marginRight: '12px', pointerEvents: 'none' }}
                    aria-label="Public access"
                  />
                  <IonLabel style={{ margin: 0 }}>
                    <h3 style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      margin: 0,
                      color: 'var(--app-text-color)'
                    }}>
                      Public
                    </h3>
                  </IonLabel>
                </IonItem>

                <IonItem
                  button
                  onClick={async () => {
                    setAccessControl('email_verification');
                    await handleUpdateLink('email_verification', title);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setAccessControl('email_verification');
                      handleUpdateLink('email_verification', title);
                    }
                  }}
                  style={{
                    '--border-radius': '6px',
                    '--background': 'var(--app-card-background-color)',
                    '--padding-start': '12px',
                    '--padding-end': '12px',
                    '--min-height': '48px',
                    border: accessControl === 'email_verification' ? '2px solid var(--ion-color-primary)' : '1px solid var(--app-border-color)',
                    cursor: 'pointer'
                  }}
                >
                  <IonRadio
                    slot="start"
                    value="email_verification"
                    style={{ marginRight: '12px', pointerEvents: 'none' }}
                    aria-label="Email verification"
                  />
                  <IonLabel style={{ margin: 0 }}>
                    <h3 style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      margin: 0,
                      color: 'var(--app-text-color)'
                    }}>
                      Email Verification
                    </h3>
                  </IonLabel>
                </IonItem>
              </IonRadioGroup>
              <IonText>
                <p style={{
                  fontSize: '0.8rem',
                  margin: '8px 0 0 0',
                  color: 'var(--app-text-color-secondary)',
                  lineHeight: '1.3'
                }}>
                  Prefer email verification if you'd like to know who views your profile
                </p>
              </IonText>
            </div>

            {/* Share via Apps */}
            <div style={{ marginTop: '20px' }}>
              <IonLabel style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.9rem', color: 'var(--app-text-color)' }}>
                Share via
              </IonLabel>
              <div style={{ display: 'flex', gap: '12px' }}>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={handleShareGmail}
                  disabled={!shareLink || loading}
                  style={{
                    '--border-radius': '8px',
                    height: '44px',
                    flex: 1
                  }}
                >
                  <IonIcon slot="start" icon={mailOutline} />
                  Gmail
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={handleShareWhatsApp}
                  disabled={!shareLink || loading}
                  style={{
                    '--border-radius': '8px',
                    height: '44px',
                    flex: 1,
                    '--color': '#25D366',
                    '--border-color': '#25D366'
                  }}
                >
                  <IonIcon slot="start" icon={logoWhatsapp} />
                  WhatsApp
                </IonButton>
              </div>
            </div>

            <IonButton
              expand="block"
              onClick={handleCopy}
              disabled={!shareLink || loading}
              style={{
                '--border-radius': '8px',
                height: '44px',
                marginTop: '16px'
              }}
            >
              <IonIcon slot="start" icon={copyOutline} />
              Copy Link
            </IonButton>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default ShareModal;